import { useState, useEffect } from "react";
import type { UploadPlan, ResolvedUploadFile } from "@titancloud/shared";
import { Modal } from "../ui/Modal";
import { Button } from "../ui/Button";
import { JunkFilterReport } from "./JunkFilterReport";
import { Spinner } from "../ui/Spinner";
import { formatBytes } from "../../utils/formatBytes";
import { buildSuggestedName } from "../../utils/duplicateResolver";
import { listFileNames } from "../../api/files.api";

type ConflictResolution = "replace" | "rename" | "skip";

interface ConflictEntry {
  /** index into plan.files (non-junk only) */
  planIndex: number;
  existingId: string;
  originalName: string;
  suggestedName: string;
  resolution: ConflictResolution | null;
  customName: string; // the rename candidate (may be edited by user)
}

interface UploadModalProps {
  plan: UploadPlan;
  folderId: string | null;
  onConfirm: (resolved: ResolvedUploadFile[]) => void;
  onCancel: () => void;
}

// For batches larger than this, skip the per-file conflict UI entirely —
// rendering 500+ conflict cards would freeze the browser.
const LARGE_BATCH_THRESHOLD = 200;

export function UploadModal({ plan, folderId, onConfirm, onCancel }: UploadModalProps) {
  const [phase, setPhase] = useState<"loading" | "conflicts" | "summary">("loading");
  const [conflicts, setConflicts] = useState<ConflictEntry[]>([]);
  // For large batches, we auto-skip all conflicts and just show a count
  const [autoSkippedCount, setAutoSkippedCount] = useState(0);

  const includedFiles = plan.files.filter((f) => !f.isJunk && f.sizeBytes > 0);
  const isLargeBatch = includedFiles.length >= LARGE_BATCH_THRESHOLD;

  // On mount, fetch existing file names and detect conflicts
  useEffect(() => {
    listFileNames(folderId).then((existing) => {
      const existingMap = new Map(existing.map((e) => [e.name, e.id]));
      const allExistingNames = new Set(existing.map((e) => e.name));

      const found: ConflictEntry[] = [];
      for (let i = 0; i < includedFiles.length; i++) {
        // Files nested inside a dropped folder land in a new sub-folder, not
        // `folderId` itself — checking them against this folder's contents
        // could misattribute a conflict (and "replace" would trash the wrong file).
        if (includedFiles[i]!.relativePath.includes("/")) continue;

        const name = includedFiles[i]!.file.name;
        const existingId = existingMap.get(name);
        if (existingId) {
          const taken = new Set([
            ...allExistingNames,
            ...found.map((c) => c.suggestedName),
          ]);
          const suggested = buildSuggestedName(name, taken);
          found.push({
            planIndex: i,
            existingId,
            originalName: name,
            suggestedName: suggested,
            // Large batch: auto-skip so we never render 500+ conflict cards
            resolution: isLargeBatch ? "skip" : null,
            customName: suggested,
          });
        }
      }

      setConflicts(found);
      if (isLargeBatch && found.length > 0) {
        setAutoSkippedCount(found.length);
      }
      // Large batch: skip conflict UI, go straight to summary
      setPhase(found.length > 0 && !isLargeBatch ? "conflicts" : "summary");
    }).catch(() => {
      setPhase("summary");
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const setResolution = (planIndex: number, resolution: ConflictResolution) => {
    setConflicts((prev) =>
      prev.map((c) => (c.planIndex === planIndex ? { ...c, resolution } : c))
    );
  };

  const applyToAll = (resolution: ConflictResolution) => {
    setConflicts((prev) => prev.map((c) => ({ ...c, resolution })));
  };

  const setCustomName = (planIndex: number, name: string) => {
    setConflicts((prev) =>
      prev.map((c) => (c.planIndex === planIndex ? { ...c, customName: name } : c))
    );
  };

  const allResolved = conflicts.every((c) => c.resolution !== null);

  const handleProceed = () => setPhase("summary");

  // Compute effective counts for summary (after conflict resolutions)
  const conflictMap = new Map(conflicts.map((c) => [c.planIndex, c]));
  const skippedCount = conflicts.filter((c) => c.resolution === "skip").length;
  const effectiveCount = includedFiles.length - skippedCount;
  const effectiveBytes = includedFiles.reduce((sum, f, i) => {
    const c = conflictMap.get(i);
    if (c?.resolution === "skip") return sum;
    return sum + f.sizeBytes;
  }, 0);

  const handleConfirm = () => {
    const resolved: ResolvedUploadFile[] = [];

    for (let i = 0; i < includedFiles.length; i++) {
      const f = includedFiles[i]!;
      const conflict = conflictMap.get(i);

      if (conflict) {
        if (conflict.resolution === "skip") continue;
        if (conflict.resolution === "replace") {
          resolved.push({
            file: f.file,
            uploadAs: conflict.originalName,
            relativePath: f.relativePath,
            replaceId: conflict.existingId,
          });
        } else {
          // rename — use customName (the user-visible suggested name)
          resolved.push({
            file: f.file,
            uploadAs: conflict.customName || conflict.suggestedName,
            relativePath: f.relativePath,
          });
        }
      } else {
        resolved.push({ file: f.file, uploadAs: f.file.name, relativePath: f.relativePath });
      }
    }

    onConfirm(resolved);
  };

  const RESOLUTION_STYLES: Record<ConflictResolution, string> = {
    replace: "border-red-500 bg-red-900/20 text-red-300",
    rename: "border-brand-500 bg-brand-900/20 text-brand-300",
    skip: "border-gray-600 bg-gray-800 text-gray-400",
  };

  return (
    <Modal
      open
      onClose={onCancel}
      title={
        phase === "loading" ? "Checking for duplicates…"
        : phase === "conflicts" ? `${conflicts.length} Duplicate${conflicts.length !== 1 ? "s" : ""} Found`
        : "Ready to Upload"
      }
      maxWidth="max-w-2xl"
    >
      {/* ── Loading ────────────────────────────────────────────────── */}
      {phase === "loading" && (
        <div className="flex items-center justify-center py-12">
          <Spinner size="lg" />
        </div>
      )}

      {/* ── Conflict resolution ─────────────────────────────────────── */}
      {phase === "conflicts" && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-400">
              The following files already exist in this folder. Choose what to do for each:
            </p>
          </div>

          {/* Bulk actions */}
          <div className="flex items-center gap-2 rounded-lg border border-gray-700 bg-gray-800/40 px-3 py-2">
            <span className="text-xs text-gray-500">Apply to all:</span>
            <button
              onClick={() => applyToAll("skip")}
              className="rounded-md bg-gray-700 px-3 py-1 text-xs font-medium text-gray-300 transition-colors hover:bg-gray-600 hover:text-white"
            >
              Skip All
            </button>
            <button
              onClick={() => applyToAll("rename")}
              className="rounded-md bg-gray-700 px-3 py-1 text-xs font-medium text-gray-300 transition-colors hover:bg-brand-900/50 hover:text-brand-300"
            >
              Rename All
            </button>
            <button
              onClick={() => applyToAll("replace")}
              className="rounded-md bg-gray-700 px-3 py-1 text-xs font-medium text-gray-300 transition-colors hover:bg-red-900/50 hover:text-red-300"
            >
              Replace All
            </button>
          </div>

          <div className="max-h-72 space-y-3 overflow-y-auto pr-1">
            {conflicts.map((conflict) => (
              <div
                key={conflict.planIndex}
                className={`rounded-lg border p-3 transition-colors ${
                  conflict.resolution ? RESOLUTION_STYLES[conflict.resolution] : "border-gray-700 bg-gray-800/60"
                }`}
              >
                {/* File name */}
                <p className="mb-2 truncate text-sm font-medium text-gray-200">
                  {conflict.originalName}
                </p>

                {/* Three choices */}
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setResolution(conflict.planIndex, "replace")}
                    className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                      conflict.resolution === "replace"
                        ? "bg-red-600 text-white"
                        : "bg-gray-700 text-gray-300 hover:bg-red-900/40 hover:text-red-300"
                    }`}
                  >
                    <svg className="h-3.5 w-3.5" viewBox="0 0 16 16" fill="currentColor">
                      <path d="M11.534 7h3.932a.25.25 0 01.192.41l-1.966 2.36a.25.25 0 01-.384 0l-1.966-2.36a.25.25 0 01.192-.41zm-11 2h3.932a.25.25 0 00.192-.41L2.692 6.23a.25.25 0 00-.384 0L.342 8.59A.25.25 0 00.534 9z"/>
                      <path fillRule="evenodd" d="M8 3c-1.552 0-2.94.707-3.857 1.818a.5.5 0 11-.771-.636A6.002 6.002 0 0113.917 7H12.9A5.002 5.002 0 008 3zM3.1 9a5.002 5.002 0 008.757 2.182.5.5 0 11.771.636A6.002 6.002 0 012.083 9H3.1z" clipRule="evenodd"/>
                    </svg>
                    Replace existing
                  </button>

                  <button
                    onClick={() => setResolution(conflict.planIndex, "rename")}
                    className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                      conflict.resolution === "rename"
                        ? "bg-brand-600 text-white"
                        : "bg-gray-700 text-gray-300 hover:bg-brand-900/40 hover:text-brand-300"
                    }`}
                  >
                    <svg className="h-3.5 w-3.5" viewBox="0 0 16 16" fill="currentColor">
                      <path d="M15.502 1.94a.5.5 0 010 .706L14.459 3.69l-2-2L13.502.646a.5.5 0 01.707 0l1.293 1.293zm-1.75 2.456l-2-2L4.939 9.21a.5.5 0 00-.121.196l-.805 2.414a.25.25 0 00.316.316l2.414-.805a.5.5 0 00.196-.12l6.813-6.814z"/>
                      <path fillRule="evenodd" d="M1 13.5A1.5 1.5 0 002.5 15h11a1.5 1.5 0 001.5-1.5v-6a.5.5 0 00-1 0v6a.5.5 0 01-.5.5h-11a.5.5 0 01-.5-.5v-11a.5.5 0 01.5-.5H9a.5.5 0 000-1H2.5A1.5 1.5 0 001 2.5v11z" clipRule="evenodd"/>
                    </svg>
                    Upload as new
                  </button>

                  <button
                    onClick={() => setResolution(conflict.planIndex, "skip")}
                    className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                      conflict.resolution === "skip"
                        ? "bg-gray-600 text-white"
                        : "bg-gray-700 text-gray-300 hover:bg-gray-600"
                    }`}
                  >
                    <svg className="h-3.5 w-3.5" viewBox="0 0 16 16" fill="currentColor">
                      <path d="M4.646 4.646a.5.5 0 01.708 0L8 7.293l2.646-2.647a.5.5 0 01.708.708L8.707 8l2.647 2.646a.5.5 0 01-.708.708L8 8.707l-2.646 2.647a.5.5 0 01-.708-.708L7.293 8 4.646 5.354a.5.5 0 010-.708z"/>
                    </svg>
                    Skip
                  </button>
                </div>

                {/* Rename input — shows the suggested name, editable */}
                {conflict.resolution === "rename" && (
                  <div className="mt-2">
                    <input
                      value={conflict.customName}
                      onChange={(e) => setCustomName(conflict.planIndex, e.target.value)}
                      className="w-full rounded border border-brand-500/60 bg-gray-900 px-2 py-1 text-xs text-gray-100 focus:border-brand-400 focus:outline-none"
                      placeholder={conflict.suggestedName}
                      spellCheck={false}
                    />
                    <p className="mt-0.5 text-[10px] text-gray-500">
                      Suggested: {conflict.suggestedName}
                    </p>
                  </div>
                )}

                {/* Replace confirmation note */}
                {conflict.resolution === "replace" && (
                  <p className="mt-1.5 text-[10px] text-red-400/80">
                    The existing file will be moved to Trash before uploading.
                  </p>
                )}
              </div>
            ))}
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={onCancel}>Cancel</Button>
            <Button onClick={handleProceed} disabled={!allResolved}>
              Continue →
            </Button>
          </div>
        </div>
      )}

      {/* ── Summary ─────────────────────────────────────────────────── */}
      {phase === "summary" && (
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <div className="rounded-lg bg-gray-800 p-3 text-center">
              <p className="text-2xl font-bold text-white">{effectiveCount}</p>
              <p className="text-xs text-gray-400">Files to upload</p>
            </div>
            <div className="rounded-lg bg-gray-800 p-3 text-center">
              <p className="text-2xl font-bold text-brand-400">
                {formatBytes(effectiveBytes)}
              </p>
              <p className="text-xs text-gray-400">Upload size</p>
            </div>
            <div className="rounded-lg bg-green-900/40 p-3 text-center">
              <p className="text-2xl font-bold text-green-400">
                {formatBytes(plan.savedBytes)}
              </p>
              <p className="text-xs text-gray-400">Saved by filtering</p>
            </div>
          </div>

          {/* Conflict resolution summary badges */}
          {conflicts.length > 0 && (
            <div className="flex flex-wrap gap-2 text-xs">
              {conflicts.filter((c) => c.resolution === "replace").length > 0 && (
                <span className="rounded-full bg-red-900/30 px-2 py-0.5 text-red-300">
                  {conflicts.filter((c) => c.resolution === "replace").length} replacing
                </span>
              )}
              {conflicts.filter((c) => c.resolution === "rename").length > 0 && (
                <span className="rounded-full bg-brand-900/30 px-2 py-0.5 text-brand-300">
                  {conflicts.filter((c) => c.resolution === "rename").length} renamed
                </span>
              )}
              {skippedCount > 0 && (
                <span className="rounded-full bg-gray-700 px-2 py-0.5 text-gray-400">
                  {skippedCount} skipped
                </span>
              )}
            </div>
          )}

          {autoSkippedCount > 0 && (
            <div className="flex items-start gap-2 rounded-lg border border-yellow-700/40 bg-yellow-900/20 px-3 py-2 text-xs text-yellow-300">
              <svg className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" viewBox="0 0 16 16" fill="currentColor">
                <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
                <path d="M7.002 11a1 1 0 1 1 2 0 1 1 0 0 1-2 0zM7.1 4.995a.905.905 0 1 1 1.8 0l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 4.995z"/>
              </svg>
              <span>
                <strong>{autoSkippedCount} duplicate{autoSkippedCount !== 1 ? "s" : ""} auto-skipped</strong> — large batch mode.
                Upload individually to choose per-file.
              </span>
            </div>
          )}

          {plan.excludedFiles > 0 && <JunkFilterReport plan={plan} />}

          <div className="flex justify-end gap-2 pt-2">
            {conflicts.length > 0 && (
              <Button variant="ghost" onClick={() => setPhase("conflicts")}>
                ← Back
              </Button>
            )}
            <Button variant="ghost" onClick={onCancel}>Cancel</Button>
            <Button onClick={handleConfirm} disabled={effectiveCount === 0}>
              Upload {effectiveCount} file{effectiveCount !== 1 ? "s" : ""}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
